import { useEffect, useRef } from 'react';
import type { PlayerState } from '@core/types';
import { SKILL_KEYS, TRAIT_KEYS, HABIT_KEYS } from '@core/types';
import {
  ASPIRATIONS,
  CAREER_FIELDS,
  CREDENTIAL_LABELS,
  EDUCATION_PROGRAMS,
  MOTIVATIONS,
  ORIGINS,
  ASSET_CLASSES,
} from '@core/content';
import {
  STAGE_LABEL,
  annualExpenses,
  annualDebtService,
  annualTax,
  bondScore,
  debtBand,
  disposableIncome,
  grossIncome,
  investableValue,
  lifestyleCost,
  netWorth,
  partnerIncome,
  runwayMonths,
  socialHealth,
  supportNetwork,
  totalDebt,
  lifeLoad,
} from '@core/derived';
import { money } from './Hud';
import type { PanelId, Settings } from '../store';
import './Panels.css';

const TITLES: Record<Exclude<PanelId, null>, string> = {
  character: 'Character',
  career: 'Work',
  finance: 'Money',
  relationships: 'People',
  goals: 'Goals and dreams',
  timeline: 'Your story',
  settings: 'Settings',
  saves: 'Saves',
};

interface PanelProps {
  panel: Exclude<PanelId, null>;
  state: PlayerState;
  settings: Settings;
  saves: { slot: string; summary: { name: string; age: number; netWorth: number }; savedAt: number }[];
  onClose: () => void;
  onUpdateSettings: (patch: Partial<Settings>) => void;
  onSave: (slot: string) => void;
  onLoad: (slot: string) => void;
  onDelete: (slot: string) => void;
}

export function Panel(props: PanelProps) {
  const { panel, onClose } = props;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="panel__scrim" onClick={onClose} aria-hidden="true" />
      <aside
        className="panel"
        role="dialog"
        aria-label={TITLES[panel]}
        tabIndex={-1}
        ref={ref}
      >
        <header className="panel__head">
          <h2 className="panel__title">{TITLES[panel]}</h2>
          <button type="button" className="panel__close" onClick={onClose} aria-label="Close panel">
            ✕
          </button>
        </header>
        <div className="panel__body">
          {panel === 'character' && <CharacterPanel state={props.state} />}
          {panel === 'career' && <CareerPanel state={props.state} />}
          {panel === 'finance' && <FinancePanel state={props.state} />}
          {panel === 'relationships' && <PeoplePanel state={props.state} />}
          {panel === 'goals' && <GoalsPanel state={props.state} />}
          {panel === 'timeline' && <TimelinePanel state={props.state} />}
          {(panel === 'settings' || panel === 'saves') && <SettingsPanel {...props} />}
        </div>
      </aside>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Shared bits
 * ------------------------------------------------------------------ */

function Row({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="row">
      <span className="row__label">{label}</span>
      <span className={`row__value numeral${tone ? ` row__value--${tone}` : ''}`}>{value}</span>
    </div>
  );
}

function Bar({ label, value, max = 100, hue }: { label: string; value: number; max?: number; hue?: string }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="bar">
      <span className="bar__label">{label}</span>
      <span className="bar__track" aria-hidden="true">
        <span className="bar__fill" style={{ width: `${pct}%`, background: hue ?? 'var(--c-accent)' }} />
      </span>
      <span className="bar__value numeral">{Math.round(value)}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="section">
      <h3 className="section__title">{title}</h3>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Character
 * ------------------------------------------------------------------ */

function CharacterPanel({ state }: { state: PlayerState }) {
  const origin = ORIGINS[state.identity.originId];
  const motivation = MOTIVATIONS[state.identity.motivationId];

  // Traits are only partly legible: the player learns the rest by living.
  const visibleTraits = TRAIT_KEYS.slice(
    0,
    Math.min(TRAIT_KEYS.length, 4 + Math.floor((state.age - state.startAge) / 6)),
  );

  return (
    <>
      <Section title="Who you are">
        <p className="prose">
          <strong>{state.identity.name}</strong>, {state.age}. {STAGE_LABEL[state.stage]}.
        </p>
        {origin && (
          <p className="prose prose--dim">
            <span className="tag">Origin</span> {origin.label} — {origin.blurb}
          </p>
        )}
        {motivation && (
          <p className="prose prose--dim">
            <span className="tag">Drive</span> {motivation.label} — {motivation.blurb}
          </p>
        )}
        <div className="meta">
          <span>Seed {state.rng.seed}</span>
          <span>Life {state.lifeId}</span>
        </div>
      </Section>

      <Section title="Skills">
        {SKILL_KEYS.map((key) => (
          <Bar key={key} label={humanise(key)} value={state.skills[key]} />
        ))}
      </Section>

      <Section title="Tendencies">
        <p className="hintline">
          You only know yourself so well. More becomes clear as the years pass.
        </p>
        {visibleTraits.map((key) => (
          <Bar key={key} label={humanise(key)} value={state.traits[key]} hue="var(--c-purpose)" />
        ))}
      </Section>

      <Section title="Habits">
        {HABIT_KEYS.map((key) => (
          <Bar key={key} label={humanise(key)} value={state.habits[key]} hue="var(--c-info)" />
        ))}
      </Section>

      {state.conditions.length > 0 && (
        <Section title="Health conditions">
          {state.conditions.map((condition) => (
            <Row
              key={condition.id}
              label={condition.label}
              value={`since ${condition.onsetAge}${condition.chronic ? ' · chronic' : ''}`}
              tone="bad"
            />
          ))}
        </Section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Career
 * ------------------------------------------------------------------ */

function CareerPanel({ state }: { state: PlayerState }) {
  const field = state.career.fieldId ? CAREER_FIELDS[state.career.fieldId] : undefined;
  const role = field?.roles[state.career.roleLevel];
  const enrollment = state.education.enrollment
    ? EDUCATION_PROGRAMS[state.education.enrollment.programId]
    : undefined;

  return (
    <>
      <Section title="Now">
        <Row label="Status" value={humanise(state.career.status)} />
        {field && <Row label="Field" value={field.label} />}
        {role && <Row label="Role" value={role.title} />}
        <Row label="Salary" value={money(state.finance.salary)} />
        <Row label="Experience" value={`${state.career.yearsExperience} years`} />
        {field && <p className="prose prose--dim">{field.blurb}</p>}
      </Section>

      <Section title="Standing">
        <Bar label="Performance" value={state.career.performance} />
        <Bar label="Reputation" value={state.career.reputation} />
        <Bar label="Job security" value={state.career.jobSecurity} hue="var(--c-positive)" />
        <Bar label="Workload" value={state.career.workload} hue="var(--c-stress)" />
        <Bar label="Meaning" value={state.career.meaningAlignment} hue="var(--c-purpose)" />
        <Bar label="Total life load" value={lifeLoad(state)} hue="var(--c-stress)" />
      </Section>

      <Section title="Wellbeing at work">
        <Row label="Burnout stage" value={humanise(state.burnout.stage)} tone={state.burnout.load > 60 ? 'bad' : undefined} />
        <Bar label="Accumulated load" value={state.burnout.load} hue="var(--c-stress)" />
        {state.burnout.episodes > 0 && <Row label="Burnout episodes" value={String(state.burnout.episodes)} tone="bad" />}
      </Section>

      <Section title="History">
        <Row label="Promotions" value={String(state.career.promotions)} />
        <Row label="Field changes" value={String(state.career.pivots)} />
        <Row label="Redundancies" value={String(state.career.layoffs)} />
        <Row label="Career breaks" value={String(state.career.breaksTaken)} />
      </Section>

      <Section title="Education">
        <Row label="Level" value={humanise(state.education.level)} />
        {enrollment && (
          <Row
            label="Studying"
            value={`${enrollment.label} · ${state.education.enrollment?.yearsRemaining ?? 0} yrs left`}
          />
        )}
        {state.education.credentials.length === 0 ? (
          <p className="prose prose--dim">No formal credentials.</p>
        ) : (
          <ul className="chips">
            {state.education.credentials.map((id) => (
              <li key={id} className="chip">
                {CREDENTIAL_LABELS[id] ?? id}
              </li>
            ))}
          </ul>
        )}
        {state.education.totalCost > 0 && (
          <Row label="Spent on study" value={money(state.education.totalCost)} />
        )}
      </Section>

      {state.business.active && (
        <Section title="Business">
          <Row label="Name" value={state.business.name} />
          <Row label="Revenue" value={money(state.business.revenue)} />
          <Row label="Operating cost" value={money(state.business.operatingCost)} />
          <Row
            label="Profit"
            value={money(state.business.revenue - state.business.operatingCost)}
            tone={state.business.revenue - state.business.operatingCost >= 0 ? 'good' : 'bad'}
          />
          <Row label="Valuation" value={money(state.business.valuation)} />
          <Bar label="Health" value={state.business.health} hue="var(--c-positive)" />
          <Bar label="Risk" value={state.business.risk} hue="var(--c-stress)" />
          <Bar label="Time demand" value={state.business.timeDemand} hue="var(--c-stress)" />
        </Section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Finance
 * ------------------------------------------------------------------ */

function FinancePanel({ state }: { state: PlayerState }) {
  const gross = grossIncome(state);
  const tax = annualTax(state);
  const disposable = disposableIncome(state);
  const expenses = annualExpenses(state);
  const service = annualDebtService(state);
  const flow = disposable - expenses - service;
  const band = debtBand(state);

  return (
    <>
      <Section title="Position">
        <Row label="Net worth" value={money(netWorth(state))} tone={netWorth(state) >= 0 ? 'good' : 'bad'} />
        <Row label="Cash" value={money(state.finance.cash)} />
        <Row label="Investments" value={money(investableValue(state))} />
        <Row label="Retirement" value={money(state.finance.retirementAssets)} />
        {state.finance.property.owned && <Row label="Home" value={money(state.finance.property.value)} />}
        <Row label="Debt" value={money(totalDebt(state))} tone={totalDebt(state) > 0 ? 'bad' : undefined} />
        <Row
          label="Runway"
          value={`${runwayMonths(state).toFixed(1)} months`}
          tone={runwayMonths(state) < 3 ? 'bad' : runwayMonths(state) > 6 ? 'good' : undefined}
        />
      </Section>

      <Section title="This year">
        <Row label="Gross income" value={money(gross)} />
        {partnerIncome(state) > 0 && <Row label="— of which partner" value={money(partnerIncome(state))} />}
        <Row label="Tax" value={money(-tax)} />
        <Row label="After tax" value={money(disposable)} />
        <Row label="Living costs" value={money(-expenses)} />
        {service > 0 && <Row label="Debt payments" value={money(-service)} />}
        <Row label="Left over" value={money(flow)} tone={flow >= 0 ? 'good' : 'bad'} />
        <p className="hintline">
          Lifestyle tier {state.finance.lifestyleLevel} costs {money(lifestyleCost(state.finance.lifestyleLevel))} a
          year. Spending rises to meet income unless you decide otherwise.
        </p>
      </Section>

      {state.finance.debts.length > 0 && (
        <Section title={`Debts — ${band}`}>
          {state.finance.debts.map((debt) => (
            <Row
              key={debt.id}
              label={`${debt.label} (${(debt.rate * 100).toFixed(1)}%)`}
              value={money(debt.principal)}
              tone="bad"
            />
          ))}
        </Section>
      )}

      <Section title="Holdings">
        {Object.values(state.finance.holdings)
          .filter((holding) => holding.value > 0 && holding.assetId !== 'property')
          .map((holding) => {
            const asset = ASSET_CLASSES[holding.assetId];
            const gain = holding.value - holding.contributed;
            return (
              <Row
                key={holding.assetId}
                label={asset?.label ?? holding.assetId}
                value={`${money(holding.value)} (${gain >= 0 ? '+' : '−'}${money(Math.abs(gain)).replace('$', '$')})`}
                tone={gain >= 0 ? 'good' : 'bad'}
              />
            );
          })}
        {investableValue(state) === 0 && <p className="prose prose--dim">Nothing invested yet.</p>}
        <Row label="Market conditions" value={humanise(state.market.regime)} />
      </Section>

      <Section title="Lifetime">
        <Row label="Earned" value={money(state.finance.lifetimeEarnings)} />
        <Row label="Spent" value={money(state.finance.lifetimeSpending)} />
        <Row label="Peak net worth" value={money(state.finance.peakNetWorth)} />
        {(state.counters['bankruptcies'] ?? 0) > 0 && (
          <Row label="Insolvencies" value={String(state.counters['bankruptcies'])} tone="bad" />
        )}
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * People
 * ------------------------------------------------------------------ */

function PeoplePanel({ state }: { state: PlayerState }) {
  const living = state.people.filter((p) => p.alive);
  const gone = state.people.filter((p) => !p.alive);

  return (
    <>
      <Section title="Overall">
        <Bar label="Relationship health" value={socialHealth(state)} hue="var(--c-happiness)" />
        <Bar label="Support available" value={supportNetwork(state)} hue="var(--c-positive)" />
        <Row label="Status" value={humanise(state.family.maritalStatus)} />
        {state.family.unionSinceAge !== null && (
          <Row label="Together for" value={`${state.age - state.family.unionSinceAge} years`} />
        )}
        <Bar label="Household harmony" value={state.family.householdHarmony} />
      </Section>

      <Section title={`People in your life (${living.length})`}>
        {living.length === 0 && <p className="prose prose--dim">Nobody close remains.</p>}
        {living
          .slice()
          .sort((a, b) => bondScore(b) - bondScore(a))
          .map((person) => (
            <div key={person.id} className="person">
              <div className="person__head">
                <span className="person__name">{person.name}</span>
                <span className="person__kind">{humanise(person.kind)}</span>
                <span className="person__age numeral">{person.age}</span>
              </div>
              <div className="person__bars">
                <Bar label="Closeness" value={person.bond.closeness} hue="var(--c-happiness)" />
                <Bar label="Trust" value={person.bond.trust} hue="var(--c-positive)" />
                {person.bond.conflict > 20 && (
                  <Bar label="Conflict" value={person.bond.conflict} hue="var(--c-stress)" />
                )}
              </div>
              {state.age - person.lastInteractionAge > 3 && person.kind !== 'child' && (
                <p className="person__warn">
                  You have not properly spoken in {state.age - person.lastInteractionAge} years.
                </p>
              )}
            </div>
          ))}
      </Section>

      {gone.length > 0 && (
        <Section title="No longer here">
          <ul className="chips">
            {gone.map((person) => (
              <li key={person.id} className="chip chip--muted">
                {person.name} · {humanise(person.kind)}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Goals
 * ------------------------------------------------------------------ */

function GoalsPanel({ state }: { state: PlayerState }) {
  const active = state.aspirations.filter((a) => a.status === 'active');
  const done = state.aspirations.filter((a) => a.status === 'achieved');
  const lost = state.aspirations.filter((a) => a.status === 'failed' || a.status === 'abandoned');
  const openRegrets = state.regrets.filter((r) => !r.resolved);

  return (
    <>
      <Section title={`Pursuing (${active.length})`}>
        {active.length === 0 && <p className="prose prose--dim">Nothing you are actively chasing.</p>}
        {active.map((aspiration) => {
          const def = ASPIRATIONS[aspiration.defId];
          return (
            <div key={aspiration.id} className="goal">
              <div className="goal__head">
                <span className="goal__label">{def?.label ?? aspiration.defId}</span>
                <span className={`tag tag--${aspiration.kind}`}>{aspiration.kind}</span>
              </div>
              {def && <p className="prose prose--dim">{def.blurb}</p>}
              <Bar label="Progress" value={aspiration.progress} />
              {aspiration.targetAge && aspiration.targetAge > state.age && (
                <p className="hintline">Realistically, by about {aspiration.targetAge}.</p>
              )}
            </div>
          );
        })}
      </Section>

      {state.inertia.intentions.length > 0 && (
        <Section title="Things you keep meaning to do">
          <p className="hintline">
            Open intentions cost a little every year they stay open, and eventually expire.
          </p>
          {state.inertia.intentions.map((intention) => (
            <Row
              key={intention.id}
              label={intention.label}
              value={`${state.age - intention.createdAge} yrs · deferred ${intention.deferrals}×`}
              tone={intention.urgency > 55 ? 'bad' : undefined}
            />
          ))}
          <Bar label="Avoidance" value={state.inertia.pressure} hue="var(--c-stress)" />
        </Section>
      )}

      {done.length > 0 && (
        <Section title="Achieved">
          <ul className="chips">
            {done.map((aspiration) => (
              <li key={aspiration.id} className="chip chip--good">
                {ASPIRATIONS[aspiration.defId]?.label ?? aspiration.defId} · {aspiration.resolvedAge}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {state.achievements.length > 0 && (
        <Section title="Milestones">
          <ul className="chips">
            {state.achievements.map((achievement) => (
              <li key={achievement.id} className="chip chip--good">
                {achievement.label} · {achievement.age}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {lost.length > 0 && (
        <Section title="Let go of">
          <ul className="chips">
            {lost.map((aspiration) => (
              <li key={aspiration.id} className="chip chip--muted">
                {ASPIRATIONS[aspiration.defId]?.label ?? aspiration.defId}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {openRegrets.length > 0 && (
        <Section title="Regrets">
          {openRegrets.map((regret) => (
            <Row key={regret.id} label={regret.label} value={`age ${regret.age}`} tone="bad" />
          ))}
        </Section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Timeline
 * ------------------------------------------------------------------ */

function TimelinePanel({ state }: { state: PlayerState }) {
  const entries = [...state.log].reverse();
  const pending = state.scheduled;

  return (
    <>
      {pending.length > 0 && (
        <Section title="Set in motion">
          <p className="hintline">Decisions already made that have not finished happening.</p>
          {pending
            .slice()
            .sort((a, b) => a.dueAge - b.dueAge)
            .map((item) => (
              <Row
                key={item.id}
                label={item.sourceLabel}
                value={`due ${item.dueAge} (from ${item.sourceAge})`}
              />
            ))}
        </Section>
      )}

      <Section title="What happened">
        <ol className="timeline">
          {entries.map((entry, index) => (
            <li key={`${entry.age}-${index}`} className={`timeline__item timeline__item--${entry.tone}`}>
              <span className="timeline__age numeral">{entry.age}</span>
              <span className="timeline__content">
                <span className="timeline__title">{entry.title}</span>
                {entry.detail && <span className="timeline__detail">{entry.detail}</span>}
              </span>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Settings and saves
 * ------------------------------------------------------------------ */

function SettingsPanel({ state, settings, saves, onUpdateSettings, onSave, onLoad, onDelete }: PanelProps) {
  return (
    <>
      <Section title="Display">
        <Toggle
          label="Reduce motion"
          hint="Removes board panning and card animation."
          checked={settings.reducedMotion}
          onChange={(v) => onUpdateSettings({ reducedMotion: v })}
        />
        <Toggle
          label="Higher contrast"
          hint="Brightens secondary text and borders."
          checked={settings.highContrast}
          onChange={(v) => onUpdateSettings({ highContrast: v })}
        />
        <Toggle
          label="Show exact numbers"
          hint="Displays numeric values beside the wellbeing meters."
          checked={settings.showNumbers}
          onChange={(v) => onUpdateSettings({ showNumbers: v })}
        />
        <Toggle
          label="Autosave"
          hint="Saves after every step. Turn off to keep manual control."
          checked={settings.autosave}
          onChange={(v) => onUpdateSettings({ autosave: v })}
        />
      </Section>

      <Section title="Save this life">
        <div className="buttons">
          <button type="button" className="btn btn--small" onClick={() => onSave('slot-1')}>
            Save to slot 1
          </button>
          <button type="button" className="btn btn--small" onClick={() => onSave('slot-2')}>
            Slot 2
          </button>
          <button type="button" className="btn btn--small" onClick={() => onSave('slot-3')}>
            Slot 3
          </button>
        </div>
        <p className="hintline">
          Saves live in this browser. Seed <strong>{state.rng.seed}</strong> reproduces this life exactly.
        </p>
      </Section>

      <Section title="Saved lives">
        {saves.length === 0 && <p className="prose prose--dim">No saves yet.</p>}
        {saves.map((save) => (
          <div key={save.slot} className="save">
            <div className="save__info">
              <span className="save__name">{save.summary.name}</span>
              <span className="save__meta">
                {save.slot} · age {save.summary.age} · {money(save.summary.netWorth)}
              </span>
            </div>
            <div className="save__actions">
              <button type="button" className="btn btn--small btn--ghost" onClick={() => onLoad(save.slot)}>
                Load
              </button>
              <button
                type="button"
                className="btn btn--small btn--ghost btn--danger"
                onClick={() => onDelete(save.slot)}
                aria-label={`Delete ${save.slot}`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </Section>

      <Section title="About">
        <p className="prose prose--dim">
          A LIFE is a simulation, not advice. Its economy, health and relationship models are
          deliberate abstractions tuned to produce believable stories rather than accurate
          predictions.
        </p>
        <div className="meta">
          <span>Game {state.gameVersion}</span>
          <span>Content {state.contentVersion}</span>
          <span>Save schema {state.saveVersion}</span>
        </div>
      </Section>
    </>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle__box" aria-hidden="true" />
      <span className="toggle__text">
        <span className="toggle__label">{label}</span>
        <span className="toggle__hint">{hint}</span>
      </span>
    </label>
  );
}

function humanise(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[._]/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}
